/**
 * XState-based Projectile State Machine
 * Part of Sprint 0023: Unified State Observability System
 * 
 * Each projectile (grenade, thrown object, etc.) is its own actor
 * with independent lifecycle management.
 */

import { setup, assign, createActor, sendParent, type AnyActorRef } from 'xstate';
import { Vector3 } from '@babylonjs/core';
import type { ProjectileStateContext, ProjectileEvent, ProjectileDebugSnapshot } from './types';
import type { ProjectileSpec } from '../types/combat';
import { logger } from '../core/Logger';

// ============================================================================
// Projectile State Machine Definition
// ============================================================================

export const projectileMachine = setup({
  types: {
    context: {} as ProjectileStateContext,
    events: {} as ProjectileEvent,
    input: {} as { 
      projectileId: string; 
      ownerId: string; 
      spec: ProjectileSpec;
      position: Vector3;
      velocity: Vector3;
    },
  },
  actions: {
    updatePhysics: assign(({ event }) => {
      if (event.type !== 'PHYSICS_UPDATE') return {};
      return {
        position: event.position.clone(),
        velocity: event.velocity.clone(),
      };
    }),
    
    markCollided: assign({
      hasCollided: true,
    }),
    
    markStuck: assign({
      isStuck: true,
    }),
    
    startFuse: assign({
      fuseStartTime: () => performance.now() / 1000,
    }),
    
    triggerDetonation: ({ context }) => {
      logger.core.log(`[ProjectileMachine] ${context.projectileId}: Detonating at`, context.position.toString());
    },
    
    spawnDetonationParticles: ({ context }) => {
      logger.core.debug(`[ProjectileMachine] ${context.projectileId}: Spawn detonation particles`);
    },
    
    destroyMesh: ({ context }) => {
      logger.core.debug(`[ProjectileMachine] ${context.projectileId}: Destroy mesh`);
    },
    
    notifyParentOfDestruction: sendParent(({ context }) => ({
      type: 'PROJECTILE_DESTROYED' as const,
      projectileId: context.projectileId,
    })),
  },
  guards: {
    shouldStick: ({ context }) => {
      return context.spec.stickyOnImpact === true;
    },
    
    hasFuse: ({ context }) => {
      return context.spec.fuseTime !== undefined;
    },
    
    shouldDetonateOnImpact: ({ context }) => {
      return context.spec.fuseTime === undefined && context.spec.detonationRadius !== undefined;
    },
    
    isKinematic: ({ context }) => {
      return context.spec.type === 'kinematic';
    },
    
    isBallistic: ({ context }) => {
      return context.spec.type === 'ballistic';
    },
  },
}).createMachine({
  id: 'projectile',
  type: 'parallel',
  
  context: ({ input }) => ({
    projectileId: input.projectileId,
    ownerId: input.ownerId,
    specId: input.spec.assetId,
    spec: input.spec,
    position: input.position.clone(),
    velocity: input.velocity.clone(),
    spawnTime: performance.now() / 1000,
    spawnPosition: input.position.clone(),
    hasCollided: false,
    isStuck: false,
    fuseStartTime: null,
  }),
  
  states: {
    // ========================================================================
    // FLIGHT REGION
    // ========================================================================
    flight: {
      initial: 'flying',
      states: {
        flying: {
          on: {
            PHYSICS_UPDATE: {
              actions: 'updatePhysics',
            },
            
            COLLISION: [
              // Sticky projectile: stick to surface
              {
                target: 'stuck',
                guard: 'shouldStick',
                actions: ['markCollided', 'markStuck'],
              },
              // Detonation on impact
              {
                target: '#projectile.detonation.detonating',
                guard: 'shouldDetonateOnImpact',
                actions: 'markCollided',
              },
              // Just mark collision (kinematic projectiles)
              {
                actions: 'markCollided',
              },
            ],
            
            LIFETIME_EXPIRE: '#projectile.cleanup',
            DISTANCE_EXPIRE: '#projectile.cleanup',
          },
        },
        
        stuck: {
          // Projectile is stuck to a surface, waiting for fuse
          on: {
            DESTROY: '#projectile.cleanup',
          },
        },
      },
    },
    
    // ========================================================================
    // DETONATION REGION
    // ========================================================================
    detonation: {
      initial: 'armed',
      states: {
        armed: {
          // Start fuse if projectile has one
          always: {
            target: 'fusing',
            guard: 'hasFuse',
            actions: 'startFuse',
          },
          
          on: {
            DETONATE: 'detonating',
          },
        },
        
        fusing: {
          // Waiting for fuse to expire
          on: {
            FUSE_EXPIRE: 'detonating',
            DETONATE: 'detonating', // Manual detonation
          },
        },
        
        detonating: {
          entry: ['triggerDetonation', 'spawnDetonationParticles'],
          
          // Allow time for detonation animation/particles
          after: {
            100: 'done',
          },
        },
        
        done: {
          type: 'final',
        },
      },
      
      onDone: '#projectile.cleanup',
    },
    
    // ========================================================================
    // CLEANUP (final state)
    // ========================================================================
    cleanup: {
      type: 'final',
      entry: ['destroyMesh', 'notifyParentOfDestruction'],
    },
  },
});

// ============================================================================
// Actor Factory
// ============================================================================

export function createProjectileActor(
  projectileId: string,
  ownerId: string,
  spec: ProjectileSpec,
  position: Vector3,
  velocity: Vector3
) {
  return createActor(projectileMachine, {
    input: { projectileId, ownerId, spec, position, velocity },
  });
}

// ============================================================================
// Debug Snapshot Helper
// ============================================================================

export function getProjectileDebugSnapshot(actor: AnyActorRef): ProjectileDebugSnapshot {
  const snapshot = actor.getSnapshot();
  const context = snapshot.context as ProjectileStateContext;
  const stateValue = snapshot.value as { flight: string; detonation: string };
  
  const now = performance.now() / 1000;
  
  return {
    projectileId: context.projectileId,
    ownerId: context.ownerId,
    specId: context.specId,
    timestamp: now * 1000,
    
    flightState: stateValue.flight,
    detonationState: stateValue.detonation,
    
    position: context.position.clone(),
    velocity: context.velocity.clone(),
    hasCollided: context.hasCollided,
    isStuck: context.isStuck,
    
    distanceTraveled: Vector3.Distance(context.spawnPosition, context.position),
    timeAlive: now - context.spawnTime,
  };
}

// ============================================================================
// Type Exports
// ============================================================================

export type ProjectileActor = ReturnType<typeof createProjectileActor>;
export type ProjectileMachine = typeof projectileMachine;
